import React, { useState } from 'react';
import { Box, SxProps, Theme } from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';

interface ImageWithFallbackProps {
  src?: string | null;
  alt: string;
  width?: number | string;
  height?: number | string;
  borderRadius?: number | string;
  objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
  fallbackIcon?: React.ReactNode;
  fallbackBgColor?: string;
  style?: React.CSSProperties;
  sx?: SxProps<Theme>;
}

const ImageWithFallback: React.FC<ImageWithFallbackProps> = ({
  src,
  alt,
  width = 80,
  height = 80,
  borderRadius = 8,
  objectFit = 'cover',
  fallbackIcon,
  fallbackBgColor = '#f5f5f5',
  style,
  sx,
}) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const shouldShowFallback = !src || imageError;

  const handleImageError = () => {
    setImageError(true);
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  if (shouldShowFallback) {
    return (
      <Box
        sx={{
          width,
          height,
          borderRadius: `${borderRadius}px`,
          backgroundColor: fallbackBgColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid #e0e0e0',
          ...sx,
        }}
        style={style}
      >
        {fallbackIcon || (
          <ImageIcon
            sx={{
              fontSize: typeof width === 'number' ? width * 0.4 : 32,
              color: '#bdbdbd',
            }}
          />
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width,
        height,
        borderRadius: `${borderRadius}px`,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: imageLoaded ? 'transparent' : fallbackBgColor,
        ...sx,
      }}
      style={style}
    >
      <img
        src={src}
        alt={alt}
        onError={handleImageError}
        onLoad={handleImageLoad}
        style={{
          width: '100%',
          height: '100%',
          objectFit,
          display: imageLoaded ? 'block' : 'none',
        }}
      />
      {!imageLoaded && (
        <ImageIcon
          sx={{
            fontSize: typeof width === 'number' ? width * 0.4 : 32,
            color: '#bdbdbd',
          }}
        />
      )}
    </Box>
  );
};

export default ImageWithFallback;
